import type { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
  type RouterHistory,
} from "@tanstack/react-router";
import { requireOperator } from "./auth/guard";
import { InvitePage } from "./auth/InvitePage";
import { LoginPage } from "./auth/LoginPage";
import { AdminLayout } from "./admin/AdminLayout";
import { AdminInvitesPage } from "./admin/AdminInvitesPage";
import { AuditPage } from "./admin/AuditPage";
import { ErrorsPage } from "./admin/ErrorsPage";
import { OperatorsPage } from "./admin/OperatorsPage";
import { TenantsPage } from "./admin/TenantsPage";
import { WorkspaceDetailPage } from "./admin/WorkspaceDetailPage";
import { requireAdmin } from "./admin/guard";
import { RootLayout } from "./components/layout/RootLayout";
import { ConfigurationsPage } from "./features/configs/ConfigurationsPage";
import { SlotEditorPage } from "./features/configs/SlotEditorPage";
import { ApiKeysPage } from "./features/api-keys/ApiKeysPage";
import { DevicesPage } from "./features/devices/DevicesPage";
import { MembersPage } from "./features/members/MembersPage";
import { RosterPage } from "./features/roster/RosterPage";
import { UsagePage } from "./features/usage/UsagePage";
import { AppIndexPage } from "./pages/AppIndexPage";
import { PlayerPage } from "./features/player/PlayerPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { WorkspaceLayout } from "./pages/WorkspaceLayout";

export interface RouterContext {
  queryClient: QueryClient;
}

/** Root carries no chrome: each branch brings its own layout. */
const rootRoute = createRootRouteWithContext<RouterContext>()({
  notFoundComponent: NotFoundPage,
});

/** Operator shell (header and workspace switcher) around the operator pages. The player route is outside it. */
const operatorRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "operator",
  component: RootLayout,
});

const loginRoute = createRoute({
  getParentRoute: () => operatorRoute,
  path: "/login",
  validateSearch: (search: Record<string, unknown>): { invite?: string; error?: string } => ({
    ...(typeof search.invite === "string" && search.invite.length > 0
      ? { invite: search.invite }
      : {}),
    ...(typeof search.error === "string" && search.error.length > 0 ? { error: search.error } : {}),
  }),
  component: function LoginRoute() {
    const search = loginRoute.useSearch();
    return <LoginPage invite={search.invite} error={search.error} />;
  },
});

/** /invite/:token shows whose invitation it is (peek) and starts Google sign-in with the token. */
const inviteRoute = createRoute({
  getParentRoute: () => operatorRoute,
  path: "/invite/$token",
  component: function InviteRoute() {
    const { token } = inviteRoute.useParams();
    return <InvitePage token={token} />;
  },
});

/** Public landing page at /: no operator chrome. Code-split so the app bundle does not grow. */
const landingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: lazyRouteComponent(() => import("./features/landing/LandingPage"), "LandingPage"),
});

const appIndexRoute = createRoute({
  getParentRoute: () => operatorRoute,
  path: "/app",
  beforeLoad: ({ context }) => requireOperator(context.queryClient),
  component: AppIndexPage,
});

const workspaceRoute = createRoute({
  getParentRoute: () => operatorRoute,
  path: "/app/$workspaceId",
  beforeLoad: ({ context }) => requireOperator(context.queryClient),
  component: function WorkspaceRoute() {
    const { workspaceId } = workspaceRoute.useParams();
    return <WorkspaceLayout workspaceId={workspaceId} />;
  },
});

const workspaceIndexRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/",
  beforeLoad: ({ params }) => {
    // TanStack Router requires throwing redirect() from beforeLoad.
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: "/app/$workspaceId/roster", params: { workspaceId: params.workspaceId } });
  },
});

const rosterRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/roster",
  component: function RosterRoute() {
    const { workspaceId } = rosterRoute.useParams();
    return <RosterPage workspaceId={workspaceId} />;
  },
});

const configurationsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/configurations",
  component: function ConfigurationsRoute() {
    const { workspaceId } = configurationsRoute.useParams();
    return <ConfigurationsPage workspaceId={workspaceId} />;
  },
});

const configurationRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/configurations/$configurationId",
  component: function ConfigurationRoute() {
    const { workspaceId, configurationId } = configurationRoute.useParams();
    return <SlotEditorPage workspaceId={workspaceId} configurationId={configurationId} />;
  },
});

const configurationKeysRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/configurations/$configurationId/keys",
  component: function ConfigurationKeysRoute() {
    const { workspaceId, configurationId } = configurationKeysRoute.useParams();
    return <ApiKeysPage workspaceId={workspaceId} configurationId={configurationId} />;
  },
});

const membersRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/members",
  component: function MembersRoute() {
    const { workspaceId } = membersRoute.useParams();
    return <MembersPage workspaceId={workspaceId} />;
  },
});

const usageRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/usage",
  component: function UsageRoute() {
    const { workspaceId } = usageRoute.useParams();
    return <UsagePage workspaceId={workspaceId} />;
  },
});

const devicesRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/desktops",
  component: function DevicesRoute() {
    const { workspaceId } = devicesRoute.useParams();
    return <DevicesPage workspaceId={workspaceId} />;
  },
});

const playerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/p/$token",
  component: function PlayerRoute() {
    const { token } = playerRoute.useParams();
    return <PlayerPage token={token} />;
  },
});

/** Dev-only kit review page. Built and registered only in development, so production never ships it. */
const designRoute = import.meta.env.DEV
  ? createRoute({
      getParentRoute: () => rootRoute,
      path: "/design",
      component: lazyRouteComponent(() => import("./dev/DesignPage"), "DesignPage"),
    })
  : null;
const devRoutes = designRoute ? [designRoute] : [];

/** Admin area: every child needs an admin. Non-admins are sent to /app before anything renders. */
const adminRoute = createRoute({
  getParentRoute: () => operatorRoute,
  path: "/admin",
  beforeLoad: ({ context }) => requireAdmin(context.queryClient),
  component: AdminLayout,
});

const adminTenantsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/",
  component: TenantsPage,
});

const adminOperatorsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/operators",
  component: OperatorsPage,
});

const adminAuditRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/audit",
  component: AuditPage,
});

const adminErrorsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/errors",
  component: ErrorsPage,
});

const adminInvitesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/invites",
  component: AdminInvitesPage,
});

const adminWorkspaceRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: "/workspaces/$workspaceId",
  component: function AdminWorkspaceRoute() {
    const { workspaceId } = adminWorkspaceRoute.useParams();
    return <WorkspaceDetailPage workspaceId={workspaceId} />;
  },
});

const routeTree = rootRoute.addChildren([
  playerRoute,
  landingRoute,
  operatorRoute.addChildren([
    loginRoute,
    inviteRoute,
    appIndexRoute,
    workspaceRoute.addChildren([
      workspaceIndexRoute,
      rosterRoute,
      configurationsRoute,
      configurationRoute,
      configurationKeysRoute,
      membersRoute,
      usageRoute,
      devicesRoute,
    ]),
    adminRoute.addChildren([
      adminTenantsRoute,
      adminOperatorsRoute,
      adminInvitesRoute,
      adminAuditRoute,
      adminErrorsRoute,
      adminWorkspaceRoute,
    ]),
  ]),
  ...devRoutes,
]);

export function createAppRouter(queryClient: QueryClient, history?: RouterHistory) {
  return createRouter({
    routeTree,
    context: { queryClient },
    scrollRestoration: true,
    ...(history ? { history } : {}),
  });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
